import React from 'react';
import {useTranslation} from 'next-i18next';
import AnalyticCard from '@deezer/next/features/Analytic/components/AnalyticCard';
import type {TrendingArtist} from '@deezer/next/services/api/Legacy/requests/analytic/types';
import {HStack, SimpleGrid, Text} from '@tempo/core';

import Thumbnail from '@components/Thumbnail';
import {NavLink} from '@tempo/nextjs';

type TrendingArtistsProps = {
	trendingArtist?: TrendingArtist[];
	isLoading: boolean;
	isError: boolean;
};

type TrendingArtistContentProps = {
	artist: TrendingArtist;
	index: number;
};

const TrendingArtistContent = ({artist, index}: TrendingArtistContentProps) => {
	return (
		<NavLink
			href={artist.redirectUrl}
			position="relative"
			py={{base: 'spacing.m', md: 'spacing.l'}}
			ps={{base: 'spacing.l', md: 'spacing.xl'}}
			_hover={{
				bg: 'background.neutral.primary.hovered',
			}}
			// Styling for grid dividers is based on https://geary.co/internal-borders-css-grid/
			sx={{
				'&::before, &::after': {
					content: "''",
					position: 'absolute',
					backgroundColor: 'divider.neutral.primary.default',
					zIndex: 1,
				},
				'&::after': {
					inlineSize: '100%',
					blockSize: '1px',
					insetInlineStart: 0,
					insetBlockEnd: '-1px',
				},
				'&::before': {
					inlineSize: '1px',
					blockSize: '100%',
					insetBlockStart: 0,
					insetInlineEnd: '-1px',
				},
			}}
		>
			<HStack spacing={{base: 'spacing.m', md: 'spacing.xl'}}>
				<Text variant="caption.regular" color="text.neutral.primary.default">
					{String(index).padStart(2, '0')}
				</Text>
				<HStack pe="spacing.s" spacing={{base: 'spacing.s', md: 'spacing.m'}}>
					<Thumbnail
						picture={artist.picture}
						height={{base: '32px', lg: '64px'}}
						width={{base: '32px', lg: '64px'}}
						rounded={true}
						type={artist.type}
					/>
					<Text variant="caption.strong" noOfLines={1}>
						{artist.title}
					</Text>
				</HStack>
			</HStack>
		</NavLink>
	);
};

const TrendingArtists = ({
	trendingArtist,
	isError,
	isLoading,
}: TrendingArtistsProps) => {
	const {t} = useTranslation('d4c');
	return (
		<AnalyticCard
			hasNoData={!trendingArtist || !trendingArtist.length}
			title={t('analyticshome_title_trendingartists_d4c-web')}
			subtitle={t('analyticshome_text_popularartistsinlast24hours_d4c-web')}
			data-testid="trending-artists"
			isLoading={isLoading}
			isError={isError}
			variant="overview"
		>
			{trendingArtist && (
				<SimpleGrid columns={{base: 2, md: 4, xl: 5}} overflow="hidden">
					{trendingArtist.map((a, i) => (
						<TrendingArtistContent key={a.id} artist={a} index={i + 1} />
					))}
				</SimpleGrid>
			)}
		</AnalyticCard>
	);
};

export default TrendingArtists;
